// @flow
import React, { Component, createRef } from "react";
import ReactTable, { ReactTableDefaults } from "react-table";
import AlertContainer from "react-alert";
import { Icon, Label, Grid, Button } from "semantic-ui-react";
import { withTranslation } from "react-i18next";
import { saveAs } from "file-saver";
import moment from "moment";

import BrandDetail from "./BrandDetail";
import BrandGroupsFilter from "./BrandGroupsFilter";
import { SubHeader } from "./../../components";
import Service from "./service";
import CustomConfirm from "../../components/CustomConfirm";
import Can from "../Can";
import "./Brands.css";

const BRAND_USED_ON_USERS_ERROR = /brand cannot be deleted, still used on \d+ users/;

class Brands extends Component {
  state = {
    brands: [],
    brandGroups: [],
    selectedVinPrefixes: [],
    deleteError: null,
    selectedBrand: {},
    isLoadingBrands: false,
    isSubmittingDetail: false,
    isBrandDetailVisible: false,
    isDeleteConfirmationVisible: false,
    detailViewMode: "",
    isDeletingBrand: false,
    isForceDeleteConfirmed: false,
  };

  msg = createRef();
  importFileInput = createRef();

  componentDidMount() {
    this.getBrands();
    this.getBrandGroups();
  }

  renderAlert = () => {
    const props = {
      offset: 20,
      position: "top right",
      theme: "light",
      time: 10000,
      transition: "fade",
    };

    return (
      <div className="react-alert-container">
        <AlertContainer ref={this.msg} {...props} />
      </div>
    );
  };

  getBrands = () => {
    if (this.state.isLoadingBrands) {
      return;
    }
    this.setState(
      {
        isLoadingBrands: true,
      },
      () => {
        Service.getBrands()
          .then(result => {
            if (result?.data?.data?.brands) {
              this.setState({ brands: result.data.data.brands, isLoadingBrands: false });
            }
          })
          .catch(error => {
            console.error("Error getting news brands.", error);
            this.setState({ brands: [], isLoadingBrands: false });
          });
      }
    );
  };

  getBrandGroups = () => {
    Service.getBrandGroups()
      .then(result => {
        if (result?.data?.data?.brand_groups) {
          this.setState({ brandGroups: result.data.data.brand_groups });
        }
      })
      .catch(error => {
        console.error("Error getting brand groups.", error);
        this.setState({ brandGroups: [] });
      });
  };

  getBrandGroupOptions = () => {
    return this.state.brandGroups.map(group => ({ key: group.id, value: group.id, text: group.name }));
  };

  handleFilterChange = selectedVinPrefixes => {
    this.setState({ selectedVinPrefixes });
  };

  handleDeleteBrand = () => {
    let { selectedBrand, isLoadingBrands, isForceDeleteConfirmed } = this.state;

    if (isLoadingBrands || !(selectedBrand && selectedBrand.id)) {
      return;
    }

    this.setState(
      {
        isLoadingBrands: true,
        isDeletingBrand: true,
      },
      () => {
        Service.deleteBrand({ id: selectedBrand.id, is_confirmed: isForceDeleteConfirmed })
          .then(() => {
            this.setState(
              { isDeleteConfirmationVisible: false, selectedBrand: {}, deleteError: null, isForceDeleteConfirmed: false, isLoadingBrands: false, isDeletingBrand: false },
              () => {
                this.getBrands();
              }
            );
          })
          .catch(error => {
            let err = error.response && error.response.data && error.response.data.errors ? error.response.data.errors[0] : "Error deleting brand";

            if (!isForceDeleteConfirmed && BRAND_USED_ON_USERS_ERROR.test(err)) {
              this.setState({ deleteError: err, isForceDeleteConfirmed: true, isLoadingBrands: false, isDeletingBrand: false });
              return;
            }

            this.setState({ deleteError: err, isLoadingBrands: false, isDeletingBrand: false });
          });
      }
    );
  };

  handleHideDeleteConfirmation = () => {
    this.setState({ isDeleteConfirmationVisible: false, selectedBrand: {}, deleteError: null, isForceDeleteConfirmed: false, isLoadingBrands: false });
  };

  handleShowDeleteConfirmation = brand => {
    this.setState({ isDeleteConfirmationVisible: true, selectedBrand: brand, deleteError: null, isForceDeleteConfirmed: false });
  };

  handleShowBrandDetail = options => {
    this.setState({
      detailViewMode: options.mode,
      isBrandDetailVisible: true,
      isSubmittingDetail: false,
      selectedBrand: { ...options.selectedBrand },
    });
  };

  handleHideBrandDetail = () => {
    this.setState({ isBrandDetailVisible: false, selectedBrand: {}, isSubmittingDetail: false, isLoadingBrands: false });
  };

  handleSaveBrand = (brand, photo, newGroupName) => {
    if (!brand.name.trim()) return;

    this.setState({ isSubmittingDetail: true });

    if (!newGroupName || !newGroupName.trim()) {
      this.saveBrand(brand, photo);
      return;
    }

    const name = newGroupName.trim();

    Service.createBrandGroup({ name })
      .then(response => {
        const brandGroupID = response?.data?.data?.brand_group_id;

        if (!brandGroupID) {
          this.setState({ isSubmittingDetail: false });
          return;
        }

        brand.brand_group_id = brandGroupID;
        this.setState(
          ({ brandGroups }) => (brandGroups.some(group => group.id === brandGroupID) ? null : { brandGroups: [...brandGroups, { id: brandGroupID, name }] }),
          () => this.saveBrand(brand, photo)
        );
      })
      .catch(error => {
        console.error("Error on creating brand group.", error);
        this.setState({ isSubmittingDetail: false });
      });
  };

  saveBrand = (brand, photo) => {
    if (!photo) {
      this.submitBrand(brand);
      return;
    }

    this.uploadLogo(photo).then(response => {
      if (response?.data?.data?.url) {
        brand.logo = response.data.data.url;
        this.submitBrand(brand);
      }
    });
  };

  uploadLogo = file => {
    const { selectedBrand } = this.state;

    if (!file) return;

    let fd = new FormData();
    const path = `${selectedBrand.vin_prefix}_${selectedBrand.name}`;

    fd.append("file", file);
    fd.append("path", path);

    return Service.uploadBrandLogo(fd);
  };

  handleImportFileButtonClick = () => {
    this.importFileInput.current.click();
  };

  handleImportFileChange = event => {
    const file = event.target.files[0];
    if (!file) return;

    const isODS = file.name.toLowerCase().endsWith(".ods") && file.type === "application/vnd.oasis.opendocument.spreadsheet";

    if (!isODS) {
      this.msg.current.show(this.props.t("file_must_be_ods").message || "The file must be an .ods file", { type: "error" });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    Service.importBrands(formData)
      .then(() => {
        this.msg.current.show(this.props.t("import_success").message || "Import Success", { type: "success" });
        this.getBrands();
        this.importFileInput.current.value = "";
      })
      .catch(error => {
        const errorMsg =
          typeof error.response?.data === "string" ? error.response.data : error.response.data?.errors?.[0] ?? (this.props.t("failed_import").message || "Failed Import");

        this.msg.current.show(errorMsg, { type: "error" });
        this.importFileInput.current.value = "";
      });
  };

  exportBrands = async () => {
    Service.exportBrands()
      .then(response => {
        if (response?.data?.data?.url) saveAs(response.data.data.url, `Claire_Brands_Export_${moment().format("YYYYMMDDhhss")}.ods`);
      })
      .catch(error => {
        console.error("Error Getting Export URL", error);
      });
  };

  submitBrand = brand => {
    if (this.state.detailViewMode === "create") {
      this.setState(
        {
          isSubmittingDetail: true,
        },
        () => {
          Service.createBrand(brand)
            .then(() => {
              this.handleHideBrandDetail();
              this.getBrands();
            })
            .catch(error => {
              console.error("Error on submitting brand.", error);
              this.setState({ isSubmittingDetail: false });
            });
        }
      );
    } else {
      this.setState(
        {
          isSubmittingDetail: true,
        },
        () => {
          Service.updateBrand(brand)
            .then(() => {
              this.handleHideBrandDetail();
              this.getBrands();
            })
            .catch(error => {
              console.error("Error on submitting brand.", error);
              this.setState({ isSubmittingDetail: false });
            });
        }
      );
    }
  };

  renderDeleteConfirm = () => {
    const { isForceDeleteConfirmed, deleteError } = this.state;

    const confirmMsg = isForceDeleteConfirmed
      ? `${deleteError.charAt(0).toUpperCase() + deleteError.slice(1)}. ${this.props.t("delete_anyway").message || "Delete anyway?"}`
      : this.props.t("confirm_delete_message").message || "Are you sure that you want to delete this? You can't undo this action.";

    return (
      <CustomConfirm
        type="danger"
        isOpen={this.state.isDeleteConfirmationVisible}
        handleCancel={this.handleHideDeleteConfirmation}
        handleConfirm={() => this.handleDeleteBrand()}
        isLoading={this.state.isDeletingBrand}
        confirmMsg={confirmMsg}
        error={isForceDeleteConfirmed ? null : deleteError}
      />
    );
  };

  renderTable = () => {
    let { isLoadingBrands, brands } = this.state;
    let { t } = this.props;

    if (isLoadingBrands) {
      return (
        <div className="Table__loading Loader-Placeholder">
          <div className="bounce1"></div>
          <div className="bounce2"></div>
          <div className="bounce3"></div>
          <section>{t("loading_brands").message || "Loading brands"}</section>
        </div>
      );
    } else {
      const { selectedVinPrefixes } = this.state;

      const sortedBrands = [...brands].sort((a, b) => {
        const groupCompare = (a.brand_group_name || "").localeCompare(b.brand_group_name || "");
        return groupCompare !== 0 ? groupCompare : (a.name || "").localeCompare(b.name || "");
      });

      const filteredBrands = selectedVinPrefixes.length ? sortedBrands.filter(brand => selectedVinPrefixes.includes(brand.vin_prefix)) : sortedBrands;

      return (
        <div className="BrandsTable">
          <ReactTable
            className="ReactTable -floated-table -contained-large"
            data={filteredBrands}
            showPagination={false}
            showPageSizeOptions={false}
            sortable={false}
            resizable={false}
            defaultPageSize={filteredBrands.length}
            pageSize={filteredBrands.length}
            noDataText={
              <div className="Table__no-results">
                <Icon disabled name="car" style={{ fontSize: "1.75em" }} />
                <p>{t("no_brands").message || "No brands"}</p>
              </div>
            }
            getTdProps={(state, rowInfo, column, instance) => {
              return {
                onClick: e => {
                  if (column.id !== "delete") {
                    this.handleShowBrandDetail({ mode: "edit", selectedBrand: rowInfo.original });
                  }
                },
              };
            }}
            getTrProps={(state, rowInfo, column) => {
              if (!rowInfo) {
                return {};
              }
              return {
                style: {
                  borderLeft: `4px solid ${rowInfo.original.is_active ? "green" : "orange"}`,
                },
              };
            }}
            column={{
              ...ReactTableDefaults.column,
              headerClassName: "ReactTable__column-header",
              className: "ReactTable__column",
            }}
            columns={[
              {
                id: "group",
                Header: t("group").message || "Group",
                accessor: d => d.brand_group_name || "",
              },
              {
                id: "name",
                Header: t("brand").message || "Brand",
                accessor: "name",
              },
              {
                id: "vin_prefix",
                Header: t("vin_nr").message || "Vin #",
                accessor: "vin_prefix",
              },
              {
                id: "button_label",
                Header: t("button_label").message || "Button label",
                accessor: "button_label",
              },
              {
                id: "delete",
                Header: "",
                className: "icons-snoozed",
                accessor: d => (
                  <Can I="delete" the="brands">
                    <Label color="red" className="BrandDeleteLabel" onClick={() => this.handleShowDeleteConfirmation(d)}>
                      <Icon link name="trash" />
                    </Label>
                  </Can>
                ),
                maxWidth: 55,
              },
            ]}
          />
        </div>
      );
    }
  };

  render() {
    return (
      <div className="Brand page_inner_wrapper">
        <SubHeader>
          <Grid.Column width={11}>
            <h1>{this.props.t("brands").message || "Brands"}</h1>
          </Grid.Column>
          <Grid.Column width={5} floated="right" className="brand-header-actions">
            <Can I="import" the="brands">
              <input type="file" ref={this.importFileInput} style={{ display: "none" }} onChange={this.handleImportFileChange} />
              <Button color="green" onClick={this.handleImportFileButtonClick}>
                {this.props.t("import").message || "Import"}
              </Button>
            </Can>
            <Can I="export" the="brands">
              <Button color="green" onClick={this.exportBrands}>
                {this.props.t("export").message || "Export"}
              </Button>
            </Can>
            <Button
              color="green"
              icon
              labelPosition="left"
              onClick={() => {
                this.handleShowBrandDetail({
                  mode: "create",
                  selectedBrand: { name: "", vin_prefix: "" },
                });
              }}
            >
              <Icon name="add" /> {this.props.t("add").message || "Add"}
            </Button>
          </Grid.Column>
          <Grid.Column width={16} className="brands-filter-row">
            <div className="brands-filter">
              <BrandGroupsFilter
                brands={this.state.brands}
                brandGroups={this.state.brandGroups}
                value={this.state.selectedVinPrefixes}
                onChange={this.handleFilterChange}
              />
            </div>
          </Grid.Column>
        </SubHeader>

        {this.renderTable()}
        {this.state.isDeleteConfirmationVisible && this.renderDeleteConfirm()}
        {this.state.isBrandDetailVisible && (
          <BrandDetail
            mode={this.state.detailViewMode}
            loading={this.state.isSubmittingDetail}
            brand={this.state.selectedBrand}
            brands={this.state.brands}
            brandGroupOptions={this.getBrandGroupOptions()}
            onHideDetail={this.handleHideBrandDetail}
            onSave={this.handleSaveBrand}
          />
        )}

        {this.renderAlert()}
      </div>
    );
  }
}

export default withTranslation()(Brands);
